/**
 * Centralized logging utility with environment-aware logging
 */

import { isDevelopment, getDebugConfig } from '../config/environment';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: number;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;

  /**
   * Log a message
   */
  private log(level: LogLevel, message: string, data?: any) {
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: Date.now(),
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Only log to console in development or if explicitly enabled
    if (isDevelopment() || getDebugConfig('verbose')) {
      const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
      const prefix = `[${level.toUpperCase()}]`;
      
      if (data) {
        console[consoleMethod](prefix, message, data);
      } else {
        console[consoleMethod](prefix, message);
      }
    }
  }

  /**
   * Log debug message
   */
  debug(message: string, data?: any) {
    if (getDebugConfig('verbose')) {
      this.log('debug', message, data);
    }
  }

  /**
   * Log info message
   */
  info(message: string, data?: any) {
    this.log('info', message, data);
  }

  /**
   * Log warning message
   */
  warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  /**
   * Log error message
   */
  error(message: string, data?: any) {
    this.log('error', message, data);
  }

  /**
   * Log API call
   */
  logApiCall(method: string, url: string, data?: any) {
    if (getDebugConfig('logApiCalls')) {
      this.debug(`API ${method} ${url}`, data);
    }
  }

  /**
   * Log state change
   */
  logStateChange(store: string, action: string, data?: any) {
    if (getDebugConfig('logStateChanges')) {
      this.debug(`[${store}] ${action}`, data);
    }
  }

  /**
   * Get all logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Clear all logs
   */
  clear() {
    this.logs = [];
  }
}

// Singleton instance
export const logger = new Logger();

